import type React from 'react';
import { useContext, useMemo } from 'react';
import { type RuleItemConfig, RuleList, type RuleListProps } from '../RuleList';
import { RulesContentContext } from './RulesContentContext';

export interface RulesContentListProps<T>
  extends Pick<RuleListProps<T>, 'rules' | 'draggable' | 'onOrderChange'>,
    Partial<Pick<RuleListProps<T>, 'onOpenRule' | 'onCloseRule'>> {
  renderItem: (args: {
    ruleItem: RuleItemConfig<T>;
    open: boolean;
    openRule: () => void;
    closeRule: () => void;
  }) => React.ReactNode;
  reverseRules?: boolean;
  withLeftBorder?: boolean;
}

export const RulesContentList = <T,>({
  rules,
  draggable,
  renderItem,
  onOrderChange,
  onOpenRule,
  onCloseRule,
  withLeftBorder,
  reverseRules = true,
}: RulesContentListProps<T>) => {
  const { openedRules, openRule, closeRule } = useContext(RulesContentContext);
  const reversedRules = useMemo(
    () => (reverseRules ? rules.slice().reverse() : rules),
    [rules, reverseRules]
  );

  const handleOpenRule = (ruleId: string) => {
    onOpenRule?.(ruleId);
    openRule(ruleId);
  };
  const handleCloseRule = (ruleId: string) => {
    onCloseRule?.(ruleId);
    closeRule(ruleId);
  };

  return (
    <RuleList<T>
      withLeftBorder={withLeftBorder}
      openedRules={openedRules}
      draggable={draggable}
      onOpenRule={handleOpenRule}
      onCloseRule={handleCloseRule}
      rules={reversedRules}
      renderItem={(ruleItem) =>
        renderItem({
          ruleItem,
          open: openedRules.includes(ruleItem.id),
          openRule: () => handleOpenRule(ruleItem.id),
          closeRule: () => handleCloseRule(ruleItem.id),
        })
      }
      onOrderChange={onOrderChange}
    />
  );
};

RulesContentList.Item = RuleList.Item;
